import { apiService } from "./api.service";

class AdminService {
  // ============ PRIVATE HELPER METHODS ============

  /**
   * Get CSRF token from cookie
   * @returns {string|null} CSRF token if found, null otherwise
   */
  getCsrfTokenFromCookie() {
    try {
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (
          name === "XSRF-TOKEN" ||
          name === "csrf_token" ||
          name === "_csrf"
        ) {
          return decodeURIComponent(value);
        }
      }
    } catch (error) {
      console.warn("Error reading CSRF token from cookie:", error);
    }
    return null;
  }

  /**
   * Get authentication token from cookie
   * @returns {string|null} Token if found, null otherwise
   */
  getAuthTokenFromCookie() {
    try {
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === "admin_access_token") {
          return decodeURIComponent(value);
        }
      }
    } catch (error) {
      console.warn("Error reading auth token from cookie:", error);
    }
    return null;
  }

  /**
   * Get admin user data from localStorage
   * @returns {Object|null} User object if found, null otherwise
   */
  getAdminUserFromStorage() {
    try {
      const userStr = localStorage.getItem("admin_user");
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if authenticated
   */
  isAuthenticated() {
    try {
      const token = this.getAuthTokenFromCookie();
      const userStr = localStorage.getItem("admin_user");

      if (!token) {
        return false;
      }

      if (!userStr) {
        return false;
      }

      try {
        const user = JSON.parse(userStr);
        const hasValidId = !!(user && (user.id || user._id || user.userId));

        if (!hasValidId) {
          return false;
        }

        return true;
      } catch (parseError) {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Ensure authentication before making request
   * @throws {Error} If not authenticated
   */
  ensureAuthenticated() {
    if (!this.isAuthenticated()) {
      throw new Error("Not authenticated. Please login first.");
    }
  }

  /**
   * Get request config with proper authentication and CSRF token
   * @param {Object} customConfig - Custom configuration
   * @returns {Object} Request configuration
   */
  getRequestConfig(customConfig = {}) {
    const token = this.getAuthTokenFromCookie();
    const csrfToken = this.getCsrfTokenFromCookie();

    const config = {
      withCredentials: true,
      ...customConfig,
      headers: {
        ...customConfig.headers,
      },
    };

    // Add Authorization header if token exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token header if exists - both header formats for compatibility
    if (csrfToken) {
      config.headers["X-CSRF-TOKEN"] = csrfToken;
      config.headers["X-XSRF-TOKEN"] = csrfToken;
    }

    return config;
  }

  /**
   * Handle API errors and retry logic
   * @param {Function} requestFn - The API request function
   * @param {number} retries - Number of retry attempts
   * @returns {Promise<any>} API response
   */
  async withRetry(requestFn, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await requestFn();
      } catch (error) {
        // If CSRF token error, refresh and retry
        if (
          error?.response?.status === 403 &&
          (error?.response?.data?.message?.includes("CSRF") ||
            error?.response?.data?.error?.includes("CSRF"))
        ) {
          await this.getCsrfToken();

          if (i < retries - 1) {
            continue;
          }
        }

        throw error;
      }
    }
  }

  /**
   * Get a new CSRF token from the server
   */
  async getCsrfToken() {
    try {
      const response = await apiService.get("/admin/csrf-token", {
        withCredentials: true,
      });

      if (response.success) {
        return response.data.csrfToken;
      }
    } catch (error) {
      console.warn("Could not get CSRF token:", error);
    }
    return null;
  }

  /**
   * Ensure CSRF token is available before making POST/PUT/DELETE requests
   */
  async ensureCsrfToken() {
    try {
      const csrfToken = this.getCsrfTokenFromCookie();
      if (!csrfToken) {
        await this.getCsrfToken();
      }
    } catch (error) {
      console.warn("Error ensuring CSRF token:", error);
    }
  }

  // ============ AUTH ENDPOINTS ============

  async login(credentials) {
    try {
      // First get a CSRF token before login
      await this.getCsrfToken();

      const response = await apiService.post("/admin/login", credentials, {
        withCredentials: true,
      });

      if (response.success && response.data?.admin) {
        // Add avatar_url to the admin data before storing
        const adminData = {
          ...response.data.admin,
          avatar_url: response.data.admin.id
            ? this.getAvatarUrl(response.data.admin.id)
            : null,
        };

        // Store user data in localStorage
        localStorage.setItem("admin_user", JSON.stringify(adminData));

        // Store permissions if available
        if (response.data.admin.permissions) {
          localStorage.setItem(
            "admin_permissions",
            JSON.stringify(response.data.admin.permissions),
          );
        }
      }

      return response;
    } catch (error) {
      return {
        success: false,
        error: "Failed to login",
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      };
    }
  }

  async logout() {
    try {
      await this.ensureCsrfToken();
      const config = this.getRequestConfig();
      const response = await apiService.post("/admin/logout", {}, config);

      // Clear auth data regardless of API response
      this.clearAuthData();

      return response;
    } catch (error) {
      // Still clear auth data on error
      this.clearAuthData();

      return {
        success: false,
        error: "Failed to logout",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Clear authentication data
   */
  clearAuthData() {
    try {
      // Clear token cookie
      document.cookie =
        "admin_access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

      // Clear CSRF token cookies
      document.cookie =
        "XSRF-TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie =
        "csrf_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie =
        "_csrf=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

      // Clear user data from localStorage
      localStorage.removeItem("admin_user");

      // Clear any other admin-related data
      localStorage.removeItem("admin_permissions");
      localStorage.removeItem("admin_last_activity");
    } catch (error) {
      console.warn("Error clearing auth data:", error);
    }
  }

  /**
   * Handle authentication error
   */
  handleAuthError(error) {
    // Clear auth data if token is invalid
    if (error?.response?.status === 401) {
      this.clearAuthData();
      // Redirect to login page
      window.location.href = "/admin/login";
    }
  }

  // ============ PROFILE ENDPOINTS ============

  async getProfile() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();
      const response = await this.withRetry(() =>
        apiService.get("/admin/profile", config),
      );

      if (response && response.success && response.data) {
        // Add avatar_url to the profile data
        const profileData = {
          ...response.data,
          avatar_url: response.data.id
            ? this.getAvatarUrl(response.data.id)
            : null,
        };

        // Update localStorage with fresh user data
        localStorage.setItem("admin_user", JSON.stringify(profileData));

        const formattedData = this.formatForUI(profileData);
        return {
          success: true,
          data: formattedData,
        };
      } else {
        return {
          success: false,
          error: response?.message || "Failed to fetch profile",
          message: response?.message || "Failed to fetch profile",
        };
      }
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to fetch profile",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async updateProfile(data) {
    try {
      this.ensureAuthenticated();

      // Validate data
      const validation = this.validateProfileData(data);
      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
          errors: validation.errors,
        };
      }

      // Ensure CSRF token before POST/PUT/DELETE
      await this.ensureCsrfToken();

      // Format for API
      const apiData = this.formatProfileForAPI(data);
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put("/admin/profile", apiData, config),
      );

      if (response.success && response.data) {
        // Update localStorage with new user data
        const currentUser = this.getAdminUserFromStorage() || {};
        const updatedUser = {
          ...currentUser,
          ...response.data,
          avatar_url: response.data.id
            ? this.getAvatarUrl(response.data.id)
            : currentUser.avatar_url,
        };
        localStorage.setItem("admin_user", JSON.stringify(updatedUser));

        response.data = this.formatForUI(updatedUser);
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to update profile",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async updatePassword(data) {
    try {
      this.ensureAuthenticated();
      // Validate data
      const validation = this.validatePasswordData(data);
      if (!validation.isValid) {
        return {
          success: false,
          error: "Validation failed",
          message: validation.errors.join(", "),
          errors: validation.errors,
        };
      }

      // Ensure CSRF token before POST/PUT/DELETE
      await this.ensureCsrfToken();

      // Format for API
      const apiData = this.formatPasswordForAPI(data);
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put("/admin/profile/password", apiData, config),
      );

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to update password",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ PROFILE AVATAR MANAGEMENT ============

  /**
   * Upload profile avatar for current user
   * @param {File} file - Image file to upload
   * @returns {Promise<Object>} Response
   */
  async uploadProfileAvatar(file) {
    try {
      this.ensureAuthenticated();
      await this.ensureCsrfToken();

      const formData = new FormData();
      formData.append("avatar", file);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.post("/admin/profile/avatar", formData, config),
      );

      if (response.success) {
        // Update user in localStorage with avatar flag
        const currentUser = this.getAdminUserFromStorage();
        if (currentUser?.id) {
          const updatedUser = {
            ...currentUser,
            has_avatar: true,
            avatar_url: this.getAvatarUrl(currentUser.id),
          };
          localStorage.setItem("admin_user", JSON.stringify(updatedUser));
        }
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to upload profile avatar",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Update profile avatar for current user
   * @param {File} file - Image file to update
   * @returns {Promise<Object>} Response
   */
  async updateProfileAvatar(file) {
    try {
      this.ensureAuthenticated();
      await this.ensureCsrfToken();

      const formData = new FormData();
      formData.append("avatar", file);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.put("/admin/profile/avatar", formData, config),
      );

      if (response.success) {
        // Update user in localStorage with avatar flag
        const currentUser = this.getAdminUserFromStorage();
        if (currentUser?.id) {
          const updatedUser = {
            ...currentUser,
            has_avatar: true,
            avatar_url: this.getAvatarUrl(currentUser.id),
          };
          localStorage.setItem("admin_user", JSON.stringify(updatedUser));
        }
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to update profile avatar",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Delete profile avatar for current user
   * @returns {Promise<Object>} Response
   */
  async deleteProfileAvatar() {
    try {
      this.ensureAuthenticated();
      await this.ensureCsrfToken();

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.delete("/admin/profile/avatar", config),
      );

      if (response.success) {
        // Update user in localStorage - remove avatar
        const currentUser = this.getAdminUserFromStorage();
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            has_avatar: false,
            avatar_url: null,
          };
          localStorage.setItem("admin_user", JSON.stringify(updatedUser));
        }
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to delete profile avatar",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Get profile avatar metadata
   * @param {string|null} adminId - Admin ID (optional, defaults to current user)
   * @returns {Promise<Object>} Response
   */
  async getProfileAvatar(adminId = null) {
    try {
      this.ensureAuthenticated();

      const config = this.getRequestConfig();
      const url = adminId
        ? `/admin/profile/avatar/${adminId}`
        : "/admin/profile/avatar";

      const response = await this.withRetry(() => apiService.get(url, config));

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to get profile avatar",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Get profile avatar image data (binary)
   * @param {string|null} adminId - Admin ID (optional, defaults to current user)
   * @returns {Promise<Blob>} Image blob
   */
  async getProfileAvatarData(adminId = null) {
    try {
      this.ensureAuthenticated();

      const config = this.getRequestConfig({
        responseType: "blob",
      });

      const url = adminId
        ? `/admin/profile/avatar/${adminId}/data`
        : "/admin/profile/avatar/data";

      const response = await this.withRetry(() => apiService.get(url, config));

      return response;
    } catch (error) {
      this.handleAuthError(error);
      throw error;
    }
  }

  // ============ ADMIN AVATAR MANAGEMENT (for super admins) ============

  /**
   * Upload avatar for specific admin (super admin only)
   * @param {string} adminId - Admin ID
   * @param {File} file - Image file
   * @returns {Promise<Object>} Response
   */
  async uploadAdminAvatar(adminId, file) {
    try {
      this.ensureAuthenticated();
      await this.ensureCsrfToken();

      const formData = new FormData();
      formData.append("avatar", file);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.post(`/admin/admins/${adminId}/avatar`, formData, config),
      );

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to upload admin avatar",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Update avatar for specific admin (super admin only)
   * @param {string} adminId - Admin ID
   * @param {File} file - Image file
   * @returns {Promise<Object>} Response
   */
  async updateAdminAvatar(adminId, file) {
    try {
      this.ensureAuthenticated();
      await this.ensureCsrfToken();

      const formData = new FormData();
      formData.append("avatar", file);

      const config = this.getRequestConfig({
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await this.withRetry(() =>
        apiService.put(`/admin/admins/${adminId}/avatar`, formData, config),
      );

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to update admin avatar",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  /**
   * Delete avatar for specific admin (super admin only)
   * @param {string} adminId - Admin ID
   * @returns {Promise<Object>} Response
   */
  async deleteAdminAvatar(adminId) {
    try {
      this.ensureAuthenticated();
      await this.ensureCsrfToken();

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.delete(`/admin/admins/${adminId}/avatar`, config),
      );

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to delete admin avatar",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ ADMIN MANAGEMENT ENDPOINTS ============

  async listAdmins(params = {}) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig({ params });

      const response = await this.withRetry(() =>
        apiService.get("/admin/admins", config),
      );

      if (response.success && response.data?.admins) {
        // Format each admin and ensure avatar_url is set
        response.data.admins = response.data.admins.map((admin) => {
          // First format with formatForUI
          const formattedAdmin = this.formatForUI(admin);

          // Double-check and force avatar_url if id exists
          if (admin.id && !formattedAdmin.avatar_url) {
            formattedAdmin.avatar_url = this.getAvatarUrl(admin.id);
          }

          return formattedAdmin;
        });
      }

      return response;
    } catch (error) {
      // Handle specific error cases
      if (
        error.response?.status === 403 &&
        error.response?.data?.message?.includes("CSRF")
      ) {
        this.handleAuthError(error);
      }

      return {
        success: false,
        error: "Failed to list admins",
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      };
    }
  }

  async getAdmin(id) {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get(`/admin/admins/${id}`, config),
      );

      if (response.success && response.data) {
        response.data = {
          ...response.data,
          avatar_url: response.data.id
            ? this.getAvatarUrl(response.data.id)
            : null,
        };
        response.data = this.formatForUI(response.data);
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to get admin",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async createAdmin(data) {
    try {
      this.ensureAuthenticated();

      await this.ensureCsrfToken();

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.post("/admin/admins", data, config),
      );

      if (response.success && response.data) {
        response.data = {
          ...response.data,
          avatar_url: response.data.id
            ? this.getAvatarUrl(response.data.id)
            : null,
        };
        response.data = this.formatForUI(response.data);
      }

      return response;
    } catch (error) {
      // If it's a CSRF error, try to refresh token and retry once
      if (
        error.response?.status === 403 &&
        (error.response?.data?.message?.includes("CSRF") ||
          error.response?.data?.error?.includes("CSRF"))
      ) {
        try {
          // Clear old CSRF token
          document.cookie =
            "XSRF-TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

          // Get new CSRF token
          await this.getCsrfToken();

          // Retry with new config
          const retryConfig = this.getRequestConfig();
          const retryResponse = await apiService.post(
            "/admin/admins",
            data,
            retryConfig,
          );
          return retryResponse;
        } catch (retryError) {
          console.error("Retry also failed:", retryError);
        }
      }

      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to create admin",
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      };
    }
  }

  async updateAdmin(id, data) {
    try {
      this.ensureAuthenticated();

      // Ensure CSRF token before POST/PUT/DELETE
      await this.ensureCsrfToken();

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/admin/admins/${id}`, data, config),
      );

      if (response.success && response.data) {
        response.data = {
          ...response.data,
          avatar_url: response.data.id
            ? this.getAvatarUrl(response.data.id)
            : null,
        };
        response.data = this.formatForUI(response.data);
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to update admin",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async deleteAdmin(id) {
    try {
      this.ensureAuthenticated();

      // Ensure CSRF token before POST/PUT/DELETE
      await this.ensureCsrfToken();

      const config = this.getRequestConfig();

      return await this.withRetry(() =>
        apiService.delete(`/admin/admins/${id}`, config),
      );
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to delete admin",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async updateStatus(id, isActive) {
    try {
      this.ensureAuthenticated();

      // Ensure CSRF token before POST/PUT/DELETE
      await this.ensureCsrfToken();

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(`/admin/admins/${id}/status`, { isActive }, config),
      );

      if (response.success && response.data) {
        response.data = {
          ...response.data,
          avatar_url: response.data.id
            ? this.getAvatarUrl(response.data.id)
            : null,
        };
        response.data = this.formatForUI(response.data);
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to update admin status",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async updatePermissions(id, permissions) {
    try {
      this.ensureAuthenticated();

      // Ensure CSRF token before POST/PUT/DELETE
      await this.ensureCsrfToken();

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put(
          `/admin/admins/${id}/permissions`,
          { permissions },
          config,
        ),
      );

      if (response.success && response.data) {
        response.data = {
          ...response.data,
          avatar_url: response.data.id
            ? this.getAvatarUrl(response.data.id)
            : null,
        };
        response.data = this.formatForUI(response.data);
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to update admin permissions",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async bulkUpdate(ids, data) {
    try {
      this.ensureAuthenticated();

      // Ensure CSRF token before POST/PUT/DELETE
      await this.ensureCsrfToken();

      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.put("/admin/admins/bulk/update", { ids, ...data }, config),
      );

      if (response.success && response.data?.admins) {
        response.data.admins = response.data.admins.map((admin) => ({
          ...admin,
          avatar_url: admin.id ? this.getAvatarUrl(admin.id) : null,
        }));
        response.data.admins = response.data.admins.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to bulk update admins",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ SESSION ENDPOINTS ============

  async listSessions() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      return await this.withRetry(() =>
        apiService.get("/admin/sessions", config),
      );
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to list sessions",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async revokeSession(sessionId) {
    try {
      this.ensureAuthenticated();

      // Ensure CSRF token before POST/PUT/DELETE
      await this.ensureCsrfToken();

      const config = this.getRequestConfig();

      return await this.withRetry(() =>
        apiService.delete(`/admin/sessions/${sessionId}`, config),
      );
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to revoke session",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ STATS ENDPOINTS ============

  async getStats() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      return await this.withRetry(() => apiService.get("/admin/stats", config));
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to fetch stats",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ ROLE-BASED ENDPOINTS ============

  async getSuperAdminEndpoint() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get("/admin/admins/role/super-admin", config),
      );

      if (response.success && response.data?.admins) {
        response.data.admins = response.data.admins.map((admin) => ({
          ...admin,
          avatar_url: admin.id ? this.getAvatarUrl(admin.id) : null,
        }));
        response.data.admins = response.data.admins.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to fetch super admins",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  async getAdminEndpoint() {
    try {
      this.ensureAuthenticated();
      const config = this.getRequestConfig();

      const response = await this.withRetry(() =>
        apiService.get("/admin/admins/role/admin", config),
      );

      if (response.success && response.data?.admins) {
        response.data.admins = response.data.admins.map((admin) => ({
          ...admin,
          avatar_url: admin.id ? this.getAvatarUrl(admin.id) : null,
        }));
        response.data.admins = response.data.admins.map(
          this.formatForUI.bind(this),
        );
      }

      return response;
    } catch (error) {
      this.handleAuthError(error);

      return {
        success: false,
        error: "Failed to fetch admins",
        message: error.message,
        status: error.response?.status,
      };
    }
  }

  // ============ UTILITY METHODS ============

  /**
   * Get avatar URL from API endpoint
   * @param {string} adminId - Admin ID
   * @returns {string|null} Avatar URL or null
   */
  getAvatarUrl(adminId) {
    if (!adminId) return null;

    const baseUrl = import.meta.env.VITE_API_URL;
    // Add a timestamp to prevent caching
    return `${baseUrl}/api/admin/profile/avatar/${adminId}/data?t=${Date.now()}`;
  }

  /**
   * Check if current user has specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} True if user has permission
   */
  hasPermission(permission) {
    try {
      const user = this.getAdminUserFromStorage();
      if (!user || !user.permissions) return false;

      return user.permissions.includes(permission);
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if current user has role
   * @param {string} role - Role to check
   * @returns {boolean} True if user has role
   */
  hasRole(role) {
    try {
      const user = this.getAdminUserFromStorage();
      if (!user) return false;

      return user.role === role;
    } catch (error) {
      return false;
    }
  }

  /**
   * Check if current user is super admin
   * @returns {boolean} True if user is super admin
   */
  isSuperAdmin() {
    return this.hasRole("super_admin");
  }

  /**
   * Get current user
   * @returns {Object|null} Current user object
   */
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem("admin_user");
      if (!userStr) return null;

      const user = JSON.parse(userStr);

      // Ensure avatar_url is set
      if (user.id && !user.avatar_url) {
        user.avatar_url = this.getAvatarUrl(user.id);
      }

      // Format the user data to ensure it has all needed fields
      return this.formatForUI(user);
    } catch (error) {
      return null;
    }
  }

  /**
   * Generate a random password
   * @param {number} length - Password length
   * @returns {string} Generated password
   */
  generateRandomPassword(length = 12) {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";

    // Ensure at least one of each required character type
    password += "ABCDEFGHIJKLMNOPQRSTUVWXYZ".charAt(
      Math.floor(Math.random() * 26),
    ); // uppercase
    password += "abcdefghijklmnopqrstuvwxyz".charAt(
      Math.floor(Math.random() * 26),
    ); // lowercase
    password += "0123456789".charAt(Math.floor(Math.random() * 10)); // number
    password += "!@#$%^&*".charAt(Math.floor(Math.random() * 8)); // special

    // Fill the rest
    for (let i = 4; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    // Shuffle the password
    return password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  }

  // ============ VALIDATION METHODS ============

  validateProfileData(data) {
    const errors = [];

    if (!data.full_name || data.full_name.trim().length < 2) {
      errors.push("Name must be at least 2 characters");
    }

    if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) {
      errors.push("Valid email is required");
    }

    if (data.phone && !/^[\d\s\-+()]*$/.test(data.phone)) {
      errors.push("Phone number is invalid");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  validatePasswordData(data) {
    const errors = [];

    if (!data.current_password || data.current_password.trim().length < 1) {
      errors.push("Current password is required");
    }

    if (!data.new_password || data.new_password.length < 8) {
      errors.push("New password must be at least 8 characters");
    }

    if (data.new_password !== data.confirm_password) {
      errors.push("Passwords do not match");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  validateAvatarFile(file) {
    const errors = [];
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (!file) {
      errors.push("No file selected");
      return { isValid: false, errors };
    }

    if (!allowedTypes.includes(file.type)) {
      errors.push(
        "Invalid file type. Please upload an image (JPEG, PNG, GIF, WEBP)",
      );
    }

    if (file.size > maxSize) {
      errors.push("File size too large. Maximum size is 5MB");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  formatProfileForAPI(uiData) {
    const apiData = {
      full_name: uiData.full_name?.trim() || "",
      email: uiData.email?.trim() || "",
    };

    // Handle phone field - use phone_number from formData
    if (uiData.phone) {
      apiData.phone_number = uiData.phone.trim();
    } else if (uiData.phone_number) {
      apiData.phone_number = uiData.phone_number.trim();
    }

    return apiData;
  }

  formatPasswordForAPI(uiData) {
    return {
      current_password: uiData.current_password,
      new_password: uiData.new_password,
      confirm_password: uiData.confirm_password,
    };
  }

  formatForUI(apiData) {
    const data = apiData.data || apiData;

    // Generate avatar URL if id exists
    let avatarUrl = null;
    if (data.id) {
      avatarUrl = this.getAvatarUrl(data.id);
    }

    return {
      id: data.id || data._id || "",
      full_name: data.full_name || data.name || data.username || "",
      email: data.email || "",
      phone: data.phone_number || data.phone || "",
      phone_number: data.phone_number || data.phone || "",
      avatar: data.avatar || null,
      avatar_url: avatarUrl, // Always generate the URL from the ID
      role: data.role || "admin",
      is_active: data.is_active !== undefined ? Boolean(data.is_active) : true,
      permissions: Array.isArray(data.permissions) ? data.permissions : [],
      created_at: data.created_at || data.createdAt || null,
      updated_at: data.updated_at || data.updatedAt || null,
      last_login_at: data.last_login_at || data.lastLogin || null,
    };
  }
}

export const adminService = new AdminService();
